import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../../../firebase/config';
import SharedHeader from '../../../../../components/SharedHeader';
import DashboardFooter from '../../../../../components/DashboardFooter';
import { FaCalendar, FaSearch, FaFileAlt, FaBook, FaCrown, FaChalkboardTeacher, FaUserTie, FaTimes } from 'react-icons/fa';
import './transactions.css';

function TransactionLogs() {
  // # States to track active tabs and data
  const [activeMasterTab, setActiveMasterTab] = useState('payments');
  const [activeTimeTab, setActiveTimeTab] = useState('all-time');
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  
  // # Filter states
  const [searchTerm, setSearchTerm] = useState('');

  // # Add new state for modal
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // # Handle master tab click function
  const handleMasterTabClick = (tabId) => {
    setActiveMasterTab(tabId);
  };

  // # Handle time tab click function
  const handleTimeTabClick = (tabId) => {
    setActiveTimeTab(tabId);
  };

  // # Format date to Cairo timezone
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Africa/Cairo'
    }).format(date);
  };

  // # Format currency
  const formatCurrency = (amount, currency = 'EGP') => {
    if (!amount) return `0.00 ${currency}`;
    return `${Number(amount).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })} ${currency}`;
  };

  // # Get payment type style
  const getPaymentTypeStyle = (product) => {
    if (!product) return {};
    
    if (product.includes('library')) {
      return { backgroundColor: '#e3f2fd', color: '#1565c0' };
    } else if (product.includes('premium')) {
      return { backgroundColor: '#fdf2e3', color: '#c06815' };
    } else if (product.includes('mentorship')) {
      return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
    } else if (product.includes('tutor_hosting')) {
      return { backgroundColor: '#f3e5f5', color: '#7b1fa2' };
    }
    return { backgroundColor: '#f5f5f5', color: '#333' };
  };

  // # Get payment type icon
  const getPaymentTypeIcon = (product) => {
    if (!product) return <FaFileAlt />;
    
    if (product.includes('library')) {
      return <FaBook />;
    } else if (product.includes('premium')) {
      return <FaCrown />;
    } else if (product.includes('mentorship')) {
      return <FaChalkboardTeacher />;
    } else if (product.includes('tutor_hosting')) {
      return <FaUserTie />;
    }
    return <FaFileAlt />;
  };

  // # Fetch transactions based on active tab and filters
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        const financialRef = collection(db, 'financial');
        let q = financialRef;

        // # Apply time-based filters
        if (activeTimeTab === 'monthly') {
          const yearMonth = `${selectedYear}_${String(selectedMonth + 1).padStart(2, '0')}`;
          q = query(financialRef, where('__name__', '==', yearMonth));
        } else if (activeTimeTab === 'yearly') {
          const yearPrefix = selectedYear.toString();
          q = query(financialRef, 
            where('__name__', '>=', `${yearPrefix}_01`),
            where('__name__', '<=', `${yearPrefix}_12`)
          );
        } else if (activeTimeTab === 'weekly') {
          const lastWeek = new Date();
          lastWeek.setDate(lastWeek.getDate() - 7);
          // Note: Weekly filtering will be done in memory after fetching data
        }

        const querySnapshot = await getDocs(q);
        let allTransactions = [];

        querySnapshot.forEach((doc) => {
          const yearMonth = doc.id;
          const data = doc.data();

          // # Handle payments
          if (activeMasterTab === 'payments' && data.payments) {
            Object.entries(data.payments).forEach(([paymentId, paymentData]) => {
              // # Format transaction data
              const transaction = {
                id: paymentId,
                date: paymentData.metadata?.created_at,
                userEmail: paymentData.user?.email,
                totalEgp: paymentData.pay?.egp_total,
                budgetAllocation: paymentData.budget_dvd?.egp_amount,
                netEgp: paymentData.pay?.egp_net,
                product: paymentData.pay?.product,
                shares: paymentData.shares || {},
                registeredBy: paymentData.register_admin?.email
              };
              allTransactions.push(transaction);
            });
          }
          // # Handle salaries
          else if (activeMasterTab === 'salaries' && data.salaries) {
            Object.entries(data.salaries).forEach(([salaryId, salaryData]) => {
              const transaction = {
                id: salaryId,
                date: salaryData.metadata?.created_at,
                receiverName: salaryData.reciever?.name,
                receiverType: salaryData.reciever?.reciever_type,
                receiverEmail: salaryData.reciever?.email,
                amountEgp: salaryData.amount_egp,
                notes: salaryData.metadata?.notes,
                registeredBy: salaryData.register_admin?.email
              };
              allTransactions.push(transaction);
            });
          }
          // # Handle expenses
          else if (activeMasterTab === 'expenses' && data.expenses) {
            Object.entries(data.expenses).forEach(([expenseId, expenseData]) => {
              const transaction = {
                id: expenseId,
                date: expenseData.metadata?.created_at,
                category: expenseData.category,
                details: expenseData.details,
                amountNative: expenseData.amount_native_currency,
                currency: expenseData.currency,
                currencySymbol: expenseData.currency_symbol,
                egpTotal: expenseData.egp_total,
                registeredBy: expenseData.register_admin?.email
              };
              allTransactions.push(transaction);
            });
          }
        });

        // # Sort by date (newest first)
        allTransactions.sort((a, b) => new Date(b.date) - new Date(a.date));

        // # Apply search filter
        if (searchTerm) {
          allTransactions = allTransactions.filter(transaction => {
            const searchLower = searchTerm.toLowerCase();
            if (activeMasterTab === 'payments') {
              return transaction.userEmail?.toLowerCase().includes(searchLower) ||
                     transaction.registeredBy?.toLowerCase().includes(searchLower);
            } else if (activeMasterTab === 'salaries') {
              return transaction.receiverName?.toLowerCase().includes(searchLower) ||
                     transaction.receiverEmail?.toLowerCase().includes(searchLower) ||
                     transaction.notes?.toLowerCase().includes(searchLower);
            } else if (activeMasterTab === 'expenses') {
              return transaction.category?.toLowerCase().includes(searchLower) ||
                     transaction.details?.toLowerCase().includes(searchLower);
            }
            return false;
          });
        }

        setTransactions(allTransactions);
      } catch (error) {
        console.error('Error fetching transactions:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [activeMasterTab, activeTimeTab, selectedYear, selectedMonth, searchTerm]);

  // # Generate years for selector (last 5 years)
  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  
  // # Months for selector
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // # Handle payment click
  const handlePaymentClick = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  // # Close modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
  };

  return (
    <div className="trns_log_admin_transactions-container">
      <SharedHeader />
      <div className="trns_log_admin_transactions-content">
        {/* # Title section */}
        <div className="trns_log_admin_transactions-title">
          <h1>Transaction Logs</h1>
        </div>

        {/* # Master tabs navigation */}
        <div className="trns_log_admin_transactions-master-tabs">
          <button 
            className={`trns_log_master_tab ${activeMasterTab === 'payments' ? 'active' : ''}`}
            onClick={() => handleMasterTabClick('payments')}
          >
            Payments
          </button>
          <button 
            className={`trns_log_master_tab ${activeMasterTab === 'expenses' ? 'active' : ''}`}
            onClick={() => handleMasterTabClick('expenses')}
          >
            Expenses
          </button>
          <button 
            className={`trns_log_master_tab ${activeMasterTab === 'salaries' ? 'active' : ''}`}
            onClick={() => handleMasterTabClick('salaries')}
          >
            Salaries
          </button>
        </div>

        {/* # Time period tabs navigation */}
        <div className="trns_log_admin_transactions-time-tabs">
          <button 
            className={`trns_log_tab ${activeTimeTab === 'all-time' ? 'active' : ''}`}
            onClick={() => handleTimeTabClick('all-time')}
          >
            All Time
          </button>
          <button 
            className={`trns_log_tab ${activeTimeTab === 'yearly' ? 'active' : ''}`}
            onClick={() => handleTimeTabClick('yearly')}
          >
            Yearly
          </button>
          <button 
            className={`trns_log_tab ${activeTimeTab === 'monthly' ? 'active' : ''}`}
            onClick={() => handleTimeTabClick('monthly')}
          >
            Monthly
          </button>
          <button 
            className={`trns_log_tab ${activeTimeTab === 'weekly' ? 'active' : ''}`}
            onClick={() => handleTimeTabClick('weekly')}
          >
            Weekly
          </button>
        </div>

        {/* # Filters section */}
        {activeMasterTab === 'payments' && (
          <div className="trns_log_filters">
            <div className="trns_log_search">
              <FaSearch className="trns_log_search-icon" />
              <input
                type="text"
                placeholder="Search by email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {activeTimeTab === 'yearly' && (
              <div className="trns_log_year-select">
                <FaCalendar className="trns_log_calendar-icon" />
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            )}

            {activeTimeTab === 'monthly' && (
              <div className="trns_log_month-year-container">
                <div className="trns_log_month-select">
                  <FaCalendar className="trns_log_calendar-icon" />
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  >
                    {months.map((month, index) => (
                      <option key={index} value={index}>{month}</option>
                    ))}
                  </select>
                </div>
                <div className="trns_log_year-select">
                  <FaCalendar className="trns_log_calendar-icon" />
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(Number(e.target.value))}
                  >
                    {years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        {/* # Transactions table */}
        {activeMasterTab === 'payments' && (
          <div className="trns_log_table-container">
            {loading ? (
              <div className="trns_log_loading">Loading transactions...</div>
            ) : transactions.length === 0 ? (
              <div className="trns_log_no-data">No transactions found</div>
            ) : (
              <table className="trns_log_table">
                <thead>
                  <tr>
                    <th>Payment Type</th>
                    <th>Date | Time</th>
                    <th>User Email</th>
                    <th>Total (EGP)</th>
                    <th>Budget</th>
                    <th>Net (EGP)</th>
                    <th>Hadidy's Share</th>
                    <th>Ayman's Share</th>
                    <th>Nabil's Share</th>
                    <th>Hussein's Share</th>
                    <th>Hossam's Share</th>
                    <th>Registered By</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id} onClick={() => handlePaymentClick(transaction)} className="trns_log_clickable-row">
                      <td>
                        <div 
                          className="trns_log_payment-type"
                          style={getPaymentTypeStyle(transaction.product)}
                        >
                          {getPaymentTypeIcon(transaction.product)}
                          <span>{transaction.product}</span>
                        </div>
                      </td>
                      <td>{formatDate(transaction.date)}</td>
                      <td>{transaction.userEmail}</td>
                      <td>{formatCurrency(transaction.totalEgp)}</td>
                      <td>{formatCurrency(transaction.budgetAllocation)}</td>
                      <td>{formatCurrency(transaction.netEgp)}</td>
                      <td>{formatCurrency(transaction.shares?.ahmed_hadidy?.net_share_egp)}</td>
                      <td>{formatCurrency(transaction.shares?.ahmed_ayman?.net_share_egp)}</td>
                      <td>{formatCurrency(transaction.shares?.nabil_mostafa?.net_share_egp)}</td>
                      <td>{formatCurrency(transaction.shares?.ahmed_hussein?.net_share_egp)}</td>
                      <td>{formatCurrency(transaction.shares?.hossam_gomaa?.net_share_egp)}</td>
                      <td>{transaction.registeredBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Salaries table */}
        {activeMasterTab === 'salaries' && (
          <div className="trns_log_table-container">
            {loading ? (
              <div className="trns_log_loading">Loading salary records...</div>
            ) : transactions.length === 0 ? (
              <div className="trns_log_no-data">No salary records found</div>
            ) : (
              <table className="trns_log_table">
                <thead>
                  <tr>
                    <th>Date | Time</th>
                    <th>Receiver</th>
                    <th>Type</th>
                    <th>Amount (EGP)</th>
                    <th>Notes</th>
                    <th>Registered By</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id} className="trns_log_clickable-row">
                      <td>{formatDate(transaction.date)}</td>
                      <td>{transaction.receiverName}</td>
                      <td>{transaction.receiverType}</td>
                      <td>{formatCurrency(transaction.amountEgp)}</td>
                      <td>{transaction.notes}</td>
                      <td>{transaction.registeredBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Expenses table */}
        {activeMasterTab === 'expenses' && (
          <div className="trns_log_table-container">
            {loading ? (
              <div className="trns_log_loading">Loading expense records...</div>
            ) : transactions.length === 0 ? (
              <div className="trns_log_no-data">No expense records found</div>
            ) : (
              <table className="trns_log_table">
                <thead>
                  <tr>
                    <th>Date | Time</th>
                    <th>Category</th>
                    <th>Details</th>
                    <th>Amount</th>
                    <th>EGP Total</th>
                    <th>Registered By</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map(transaction => (
                    <tr key={transaction.id} className="trns_log_clickable-row">
                      <td>{formatDate(transaction.date)}</td>
                      <td>{transaction.category}</td>
                      <td>{transaction.details}</td>
                      <td>{`${transaction.currencySymbol}${transaction.amountNative}`}</td>
                      <td>{formatCurrency(transaction.egpTotal)}</td>
                      <td>{transaction.registeredBy}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      <DashboardFooter />

      {/* # Payment Details Modal */}
      {showModal && selectedPayment && (
        <div className="trns_log_modal-overlay" onClick={handleCloseModal}>
          <div className="trns_log_modal-content" onClick={e => e.stopPropagation()}>
            <button className="trns_log_modal-close" onClick={handleCloseModal}>
              <FaTimes />
            </button>
            
            <div className="trns_log_modal-header">
              <div 
                className="trns_log_payment-type"
                style={getPaymentTypeStyle(selectedPayment.product)}
              >
                {getPaymentTypeIcon(selectedPayment.product)}
                <span>{selectedPayment.product} Payment Details</span>
              </div>
            </div>

            <div className="trns_log_modal-body">
              <div className="trns_log_modal-section trns_log_modal-section-financial">
                <h3>Financial Information</h3>
                <div className="trns_log_modal-grid">
                  <div className="trns_log_modal-item">
                    <label>Total Amount</label>
                    <span className="amount total">{formatCurrency(selectedPayment.totalEgp)}</span>
                  </div>
                  <div className="trns_log_modal-item">
                    <label>Budget Allocation</label>
                    <span className="amount budget">{formatCurrency(selectedPayment.budgetAllocation)}</span>
                  </div>
                  <div className="trns_log_modal-item">
                    <label>Net Amount</label>
                    <span className="amount net">{formatCurrency(selectedPayment.netEgp)}</span>
                  </div>
                </div>

                <h3 style={{ marginTop: '2rem' }}>Team Shares</h3>
                <div className="trns_log_modal-grid">
                  {Object.entries(selectedPayment.shares || {}).map(([member, share]) => (
                    <div key={member} className="trns_log_modal-item">
                      <label>{member.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}</label>
                      <span className="amount share">{formatCurrency(share.net_share_egp)}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="trns_log_modal-section trns_log_modal-section-details">
                <h3>Transaction Details</h3>
                <div className="trns_log_modal-grid">
                  <div className="trns_log_modal-item">
                    <label>Date & Time</label>
                    <span>{formatDate(selectedPayment.date)}</span>
                  </div>
                  <div className="trns_log_modal-item">
                    <label>User Email</label>
                    <span>{selectedPayment.userEmail}</span>
                  </div>
                  <div className="trns_log_modal-item">
                    <label>Registered By</label>
                    <span>{selectedPayment.registeredBy}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransactionLogs;
